/**
 * Compact Lead Pulse widget for main dashboard (ADR-009).
 */

import React, { useEffect, useState } from "react";
import { Alert, Col, Row, Skeleton, Space, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import {
  MetricTile,
  SectionEyebrow,
  ShellSection,
  SlotCard,
  formatMoneyZar,
} from "./shared/dashboardShellPrimitives.jsx";
import { getLeadIntelligenceDashboard } from "../services/dashboardService.js";

const { Text, Title } = Typography;

function confidenceTag(confidence) {
  if (confidence === "Insufficient data") return <Tag>Insufficient data</Tag>;
  return <Tag color="blue">Estimated</Tag>;
}

export default function LeadPulseWidget({ filters } = {}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await getLeadIntelligenceDashboard(filters || {});
      if (cancelled) return;
      if (!res?.success && res?.error) {
        setError(res.error);
        setData(null);
      } else {
        setData(res?.data || res);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  if (loading) {
    return (
      <ShellSection data-testid="lead-pulse-loading">
        <SlotCard title="Lead Pulse">
          <Skeleton active paragraph={{ rows: 3 }} />
        </SlotCard>
      </ShellSection>
    );
  }

  if (error) {
    return (
      <ShellSection data-testid="lead-pulse-error">
        <Alert type="error" showIcon message="Lead Pulse failed to load" description={error} />
      </ShellSection>
    );
  }

  if (!data?.enabled || data?.widgetsEnabled === false) {
    return (
      <ShellSection data-testid="lead-pulse-disabled">
        <SlotCard title="Lead Pulse">
          <Text type="secondary">
            Lead Intelligence widgets are off. Enable{" "}
            <Text code>leads.intelligence.enabled</Text> and{" "}
            <Text code>leads.intelligence.widgets.enabled</Text> under{" "}
            <Link to="/settings?tab=leads">Settings → Leads → Lead Intelligence</Link>
            .
          </Text>
        </SlotCard>
      </ShellSection>
    );
  }

  const metrics = data.metrics || {};
  const coverage = data.coverage || {};
  const briefing = data.briefing || [];
  const risks = data.risks || [];

  const tiles = [
    {
      key: "new",
      label: "New / unworked",
      value: metrics.newUnworked?.value ?? "—",
      hint: null,
      href: "/leads?opsBucket=new",
    },
    {
      key: "needs",
      label: "Needs action",
      value: metrics.needsAction?.value ?? "—",
      hint: null,
      href: "/leads?needsAction=1",
    },
    {
      key: "pipeline",
      label: "Pipeline value",
      value: formatMoneyZar(metrics.activePipelineValue?.amount),
      hint: metrics.activePipelineValue?.dataConfidence,
      href: "/leads",
    },
    {
      key: "weighted",
      label: "Weighted pipeline",
      value: formatMoneyZar(metrics.weightedPipelineValue?.amount),
      hint: metrics.weightedPipelineValue?.dataConfidence,
      href: "/leads",
    },
    {
      key: "ev",
      label: "Expected contribution",
      value: formatMoneyZar(metrics.expectedContribution?.amount),
      hint: metrics.expectedContribution?.dataConfidence || "Lead Value",
      href: "/leads",
    },
  ];

  return (
    <ShellSection data-testid="lead-pulse-widget">
      <SlotCard
        title={
          <Space wrap>
            <span>Lead Pulse</span>
            {confidenceTag(data.dataConfidence)}
            <Tag color="cyan">
              Coverage {coverage.coveragePct != null ? `${coverage.coveragePct}%` : "—"}
            </Tag>
            <Link to="/leads">Open leads →</Link>
          </Space>
        }
      >
        <SectionEyebrow>Active pipeline · Estimated</SectionEyebrow>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {tiles.map((tile) => (
            <Col xs={24} sm={12} md={8} lg={4} xl={4} key={tile.key} flex="1 1 18%">
              <Link to={tile.href} style={{ color: "inherit" }}>
                <MetricTile data-testid={`lead-pulse-metric-${tile.key}`}>
                  <span className="metric-label">{tile.label}</span>
                  <span className="metric-value">{tile.value}</span>
                  {tile.hint ? <span className="metric-hint">{tile.hint}</span> : null}
                </MetricTile>
              </Link>
            </Col>
          ))}
        </Row>

        {briefing.length ? (
          <div data-testid="lead-pulse-briefing" style={{ marginBottom: 16 }}>
            <Title level={5} style={{ margin: "0 0 8px", color: "#005249" }}>
              Briefing
            </Title>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {briefing.slice(0, 3).map((line) => (
                <li key={line}>
                  <Text>{line}</Text>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {risks.length ? (
          <div data-testid="lead-pulse-risks">
            <Title level={5} style={{ margin: "0 0 8px", color: "#005249" }}>
              Top risks
            </Title>
            <Space wrap>
              {risks.slice(0, 3).map((risk) => (
                <Tag key={risk.code} color="orange">
                  {risk.label}
                  {risk.amount != null ? ` · ${formatMoneyZar(risk.amount)}` : ""}
                </Tag>
              ))}
            </Space>
          </div>
        ) : null}
      </SlotCard>
    </ShellSection>
  );
}
